/**
 * Subtitle overlay layout for the video editor preview pane.
 * Positions and scales text inside the object-contain frame (not the outer letterbox).
 */

import { parseAspectRatio } from '@/lib/videoExportFraming';

export const SUBTITLE_EXPORT_REF_HEIGHT = 1080;
export const SUBTITLE_MIN_FONT_SIZE = 8;
export const SUBTITLE_MAX_FONT_SIZE = 96;
export const SUBTITLE_MAX_PREVIEW_FONT_PX = 96;
export const SUBTITLE_MAX_OUTLINE_WIDTH = 20;

let _graphemeSegmenter;
function graphemeLen(text) {
  const raw = String(text || '');
  if (!raw) return 0;
  try {
    if (typeof Intl !== 'undefined' && Intl.Segmenter) {
      if (!_graphemeSegmenter) {
        _graphemeSegmenter = new Intl.Segmenter(undefined, { granularity: 'grapheme' });
      }
      return [..._graphemeSegmenter.segment(raw)].length;
    }
  } catch {
    /* ignore */
  }
  return [...raw].length;
}

function graphemeTruncate(text, maxLen, suffix = '…') {
  const raw = String(text || '');
  if (!raw) return '';
  if (graphemeLen(raw) <= maxLen) return raw;
  const suffixLen = graphemeLen(suffix);
  const keep = Math.max(1, maxLen - suffixLen);
  try {
    if (typeof Intl !== 'undefined' && Intl.Segmenter) {
      if (!_graphemeSegmenter) {
        _graphemeSegmenter = new Intl.Segmenter(undefined, { granularity: 'grapheme' });
      }
      const parts = [..._graphemeSegmenter.segment(raw)].slice(0, keep).map((s) => s.segment);
      return parts.join('') + suffix;
    }
  } catch {
    /* ignore */
  }
  return `${[...raw].slice(0, keep).join('')}${suffix}`;
}

/** Pixel rect of media fitted with object-contain inside the preview container. */
export function computeObjectContainContentRect(containerW, containerH, aspectRatio) {
  const cw = Math.max(0, Number(containerW) || 0);
  const ch = Math.max(0, Number(containerH) || 0);
  if (cw < 1 || ch < 1) {
    return { left: 0, top: 0, width: 0, height: 0 };
  }
  const mediaAr = parseAspectRatio(aspectRatio);
  const containerAr = cw / ch;
  let width;
  let height;
  if (mediaAr > containerAr) {
    width = cw;
    height = cw / mediaAr;
  } else {
    height = ch;
    width = ch * mediaAr;
  }
  return {
    left: Math.round((cw - width) / 2),
    top: Math.round((ch - height) / 2),
    width: Math.round(width),
    height: Math.round(height),
  };
}

function isPortraitAspect(aspectRatio) {
  return parseAspectRatio(aspectRatio) < 0.95;
}

/** Side padding inside the fitted frame (keeps text off edges on 9:16 / mobile). */
export function subtitlePreviewHorizontalPaddingPx(frameW, aspectRatio, viewportMinPx) {
  const w = Math.max(1, Number(frameW) || 0);
  const vmin = Number(viewportMinPx) || 800;
  let pct = 0.06;
  if (isPortraitAspect(aspectRatio)) pct = 0.1;
  if (parseAspectRatio(aspectRatio) < 0.65) pct = 0.12;
  if (vmin < 420) pct = Math.max(pct, 0.14);
  if (vmin < 380) pct = Math.max(pct, 0.16);
  return Math.max(12, Math.round(w * pct));
}

/** Viewport scale for preview (mobile smaller, desktop larger). */
export function subtitleViewportScale(viewportMinPx, frameW, aspectRatio) {
  const vmin = Number(viewportMinPx);
  const w = Number(frameW) || 0;
  let scale = 1;
  if (Number.isFinite(vmin)) {
    if (vmin < 380) scale = 0.72;
    else if (vmin < 480) scale = 0.8;
    else if (vmin < 768) scale = 0.9;
    else if (vmin < 1024) scale = 1;
    else if (vmin < 1440) scale = 1.06;
    else scale = 1.12;
  }
  if (isPortraitAspect(aspectRatio) && w > 0 && w < 420) scale *= 0.92;
  return scale;
}

/**
 * Preview font size scales linearly with export slider (1080p reference) then width-ceiling.
 */
export function subtitlePreviewFontPx(exportFontSize, frameW, frameH, viewportMinPx, aspectRatio) {
  const fs = Number(exportFontSize);
  const base = Number.isFinite(fs)
    ? Math.min(SUBTITLE_MAX_FONT_SIZE, Math.max(SUBTITLE_MIN_FONT_SIZE, fs))
    : 22;
  const h = Math.max(1, Number(frameH) || 0);
  const w = Math.max(1, Number(frameW) || 0);
  const padX = subtitlePreviewHorizontalPaddingPx(w, aspectRatio, viewportMinPx);
  const usableW = Math.max(40, w - padX * 2);
  const portrait = isPortraitAspect(aspectRatio);

  let px = (base / SUBTITLE_EXPORT_REF_HEIGHT) * h;
  px *= subtitleViewportScale(viewportMinPx, w, aspectRatio);

  const charsPerLine = portrait ? 20 : 34;
  const maxPxFromWidth = usableW / (charsPerLine * 0.52);
  px = Math.min(px, maxPxFromWidth);

  return Math.max(9, Math.min(SUBTITLE_MAX_PREVIEW_FONT_PX, px));
}

export function subtitlePreviewOutlinePx(exportOutlineWidth, frameH, viewportMinPx, aspectRatio, frameW) {
  const ow = Number(exportOutlineWidth) || 0;
  if (ow < 0.25) return 0;
  const h = Math.max(1, Number(frameH) || 0);
  let px = (ow / SUBTITLE_EXPORT_REF_HEIGHT) * h;
  px *= subtitleViewportScale(viewportMinPx, frameW, aspectRatio);
  return Math.min(SUBTITLE_MAX_OUTLINE_WIDTH, Math.max(0, px));
}

export function subtitlePreviewTextShadow(style, frameH, viewportMinPx, aspectRatio, frameW) {
  const parts = [];
  const w = Math.round(subtitlePreviewOutlinePx(
    style.outlineWidth,
    frameH,
    viewportMinPx,
    aspectRatio,
    frameW,
  ));
  if (w >= 1) {
    const c = style.outlineColor || '#000000';
    const r = Math.min(4, Math.max(1, w));
    for (let dy = -r; dy <= r; dy += 1) {
      for (let dx = -r; dx <= r; dx += 1) {
        if (dx === 0 && dy === 0) continue;
        parts.push(`${dx}px ${dy}px 0 ${c}`);
      }
    }
  }
  const glowBlur = Number(style.shadowBlur) || 0;
  if (glowBlur > 0) {
    const gc = style.shadowColor || style.color || '#ffff00';
    parts.push(`0 0 ${glowBlur}px ${gc}`);
    parts.push(`0 0 ${Math.round(glowBlur * 1.75)}px ${gc}`);
  }
  return parts.length ? parts.join(', ') : 'none';
}

function textAlignFromAnchorX(anchorX) {
  const ax = Number(anchorX);
  if (!Number.isFinite(ax)) return 'center';
  if (ax < 0.34) return 'left';
  if (ax > 0.66) return 'right';
  return 'center';
}

/** Chars that fit on one line at this font size and frame width (legacy anchor layout). */
export function subtitleCharsPerLine(frameW, fontPx, aspectRatio, viewportMinPx) {
  const w = Math.max(1, Number(frameW) || 0);
  const padX = subtitlePreviewHorizontalPaddingPx(w, aspectRatio, viewportMinPx);
  const usableW = Math.max(40, w - padX * 2);
  const px = Math.max(9, Number(fontPx) || 14);
  const portrait = isPortraitAspect(aspectRatio);
  const est = Math.floor(usableW / (px * 0.52));
  const cap = portrait ? 22 : 40;
  return Math.max(10, Math.min(cap, est));
}

/** Chars per line from the draggable layer box width (no legacy frame cap). */
export function subtitleCharsPerLineForBox(boxW, fontPx) {
  const w = Math.max(32, Number(boxW) || 0);
  const px = Math.max(9, Number(fontPx) || 14);
  return Math.max(10, Math.floor(w / (px * 0.48)));
}

/**
 * Word-wrap for preview. When truncateOverflow is false, keeps wrapping into
 * additional lines (caller should shrink font if the result exceeds maxLines).
 */
export function splitSubtitlePreviewLines(text, maxLines, charsPerLine, options = {}) {
  const { truncateOverflow = false } = options;
  const raw = String(text || '').trim();
  if (!raw) return [];
  const maxL = Math.max(1, Math.floor(Number(maxLines) || 2));
  const cpl = Math.max(8, Math.floor(Number(charsPerLine) || 24));
  if (graphemeLen(raw) <= cpl) return [raw];

  const words = raw.split(/\s+/).filter(Boolean);
  const lines = [];
  let current = '';

  for (let i = 0; i < words.length; i += 1) {
    const word = words[i];
    const candidate = current ? `${current} ${word}` : word;
    if (graphemeLen(candidate) <= cpl) {
      current = candidate;
      continue;
    }
    if (current) {
      lines.push(current);
      current = word;
    } else if (graphemeLen(word) > cpl) {
      lines.push(truncateOverflow ? graphemeTruncate(word, cpl) : word);
      current = '';
    } else {
      current = word;
    }
    if (lines.length >= maxL && truncateOverflow) {
      const tail = [current, ...words.slice(i + 1)].filter(Boolean).join(' ');
      if (tail) {
        const last = lines[maxL - 1] || '';
        const merged = last ? `${last} ${tail}` : tail;
        lines[maxL - 1] = graphemeLen(merged) > cpl ? graphemeTruncate(merged, cpl) : merged;
      }
      return lines;
    }
  }

  if (current) {
    lines.push(current);
  }
  return lines.length ? lines : [truncateOverflow ? graphemeTruncate(raw, cpl) : raw];
}

/** True when every word from source text appears in the rendered lines (no ellipsis truncation). */
export function subtitleLinesIncludeFullText(sourceText, lines) {
  const srcWords = String(sourceText || '').trim().split(/\s+/).filter(Boolean);
  if (!srcWords.length) return true;
  const rendered = (lines || []).join(' ');
  return srcWords.every((word) => rendered.includes(word));
}

/** True if any line exceeds the per-line character budget. */
export function subtitleLinesOverflowChars(lines, charsPerLine) {
  const cpl = Math.max(8, Math.floor(Number(charsPerLine) || 24));
  return (lines || []).some((line) => graphemeLen(String(line || '')) > cpl);
}

/** Layout metrics for the draggable subtitle box (explicit width, up to 2 lines). */
export function computeSubtitlePreviewLayout(style, frameW, frameH, aspectRatio, viewportMinPx, text = '') {
  const w = Math.max(1, Number(frameW) || 0);
  const padX = subtitlePreviewHorizontalPaddingPx(w, aspectRatio, viewportMinPx);
  const boxW = Math.max(48, w - padX * 2);
  const fontPx = subtitlePreviewFontPx(
    style.fontSize,
    w,
    frameH,
    viewportMinPx,
    aspectRatio,
  );
  const portrait = isPortraitAspect(aspectRatio);
  const maxLines = portrait ? 2 : 3;
  const charsPerLine = subtitleCharsPerLine(w, fontPx, aspectRatio, viewportMinPx);
  const lines = splitSubtitlePreviewLines(text, maxLines, charsPerLine);
  const ay = Math.min(0.92, Math.max(0.08, Number(style.anchorY) || 0.88));

  return {
    fontPx: Math.round(fontPx),
    fontPxExact: fontPx,
    padX,
    boxW,
    maxLines,
    lines,
    textAlign: textAlignFromAnchorX(style.anchorX),
    shadow: subtitlePreviewTextShadow(style, frameH, viewportMinPx, aspectRatio, w),
    box: {
      position: 'absolute',
      left: padX,
      width: boxW,
      maxWidth: boxW,
      top: `${ay * 100}%`,
      transform: 'translateY(-100%)',
      boxSizing: 'border-box',
    },
  };
}

/** CSS aspect-ratio value from export setting e.g. "9:16" → "9 / 16" */
export function exportAspectRatioCss(aspectRatio) {
  const s = String(aspectRatio || '16:9').trim();
  const parts = s.split(/[:/]/);
  const aw = Number(parts[0]);
  const ah = Number(parts[1]);
  if (aw > 0 && ah > 0) return `${aw} / ${ah}`;
  return '16 / 9';
}
